import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { AssignmentViewModel } from 'src/app/shared/models/autogenerated';
import { UserOidToNamePipe } from 'src/app/shared/pipes/user-oid-to-name.pipe';
import { forkJoin, map, Observable, of, Subscription, Subject } from 'rxjs';
import { AgGridAngular } from 'ag-grid-angular';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/app.state';
import { take, takeUntil } from 'rxjs/operators';
import { AssignmentsMappingRendererComponent } from '../assignments-mapping-renderer/assignments-mapping-renderer.component';
import { UserRoles } from 'src/app/shared/utils/constants';
import { AgGridDisciplineChipRendererComponent } from 'src/app/shared/components/ag-grid-chip-discipline-renderer/ag-grid-chip-discipline-renderer.component';

@Component({
  standalone: false,
  selector: 'app-assignments-contacts-table',
  templateUrl: './assignments-contacts-table.component.html',
  styleUrls: ['./assignments-contacts-table.component.scss'],
  providers: [UserOidToNamePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentsContactsTableComponent implements OnChanges, OnDestroy {
  @Input() rowData: AssignmentViewModel[] = [];
  @Output() filterByUserRole = new EventEmitter<{
    userId: string;
    role: string;
  }>();

  transformedRowData: any[] = [];
  assignmentMappings: any[] = [];
  isLoading = false;

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  private destroy$ = new Subject<void>();
  private transformSub?: Subscription;

  configStoreTableName = 'assignmentsTableContacts';

  constructor(
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private userOidToNamePipe: UserOidToNamePipe
  ) {}

  components = {
    assignmentsMappingRenderer: AssignmentsMappingRendererComponent,
    agGridDisciplineChipRendererComponent:
      AgGridDisciplineChipRendererComponent,
  };

  columnDefs: ColDef[] = [
    {
      field: 'userName',
      headerName: 'User Name',
      sortable: true,
      filter: true,
    },
    {
      field: 'role',
      headerName: 'Role',
      sortable: true,
      filter: true,
      valueGetter: ({ data }) => this.getRoleLabel(data.role),
    },
    {
      headerName: 'Disciplines',
      minWidth: 230,
      sortable: false,
      filter: true,
      valueGetter: ({ data }) =>
        data.role === UserRoles.SME ? data.disciplines?.join(', ') || '' : '',
    },
    {
      headerName: 'Map',
      colId: 'map',
      minWidth: 90,
      maxWidth: 90,
      pinned: 'left',
      filter: 'agNumberColumnFilter',
      valueGetter: ({ data }) => data.assignmentCount ?? 0,
      type: 'centerAligned',
      cellRenderer: 'assignmentsMappingRenderer',
      cellRendererParams: {
        onMappingClick: (data: any) => {
          this.filterByUserRole.emit({ userId: data.userId, role: data.role });
        },
      },
    },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['rowData']?.currentValue !== changes['rowData']?.previousValue
    ) {
      this.transformSub?.unsubscribe();
      this.isLoading = true;
      this.transformSub = this.transformData()
        .pipe(takeUntil(this.destroy$))
        .subscribe((transformed) => {
          this.transformedRowData = transformed;
          this.cdr.markForCheck();
          this.isLoading = false;
        });
    }
  }

  ngOnDestroy(): void {
    this.transformSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resolveUserNames(userIds: string[]): Observable<Map<string, string>> {
    if (!userIds.length) return of(new Map<string, string>());

    const lookups$ = userIds.map((id) =>
      this.userOidToNamePipe.transform(id).pipe(
        take(1),
        map((name) => [id, name] as [string, string])
      )
    );

    return forkJoin(lookups$).pipe(map((entries) => new Map(entries)));
  }

  transformData(): Observable<any[]> {
    if (!this.rowData?.length) return of([]);

    const uniqueIds = Array.from(
      new Set(this.rowData.map((r) => r.userId).filter(Boolean))
    );

    return this.resolveUserNames(uniqueIds).pipe(
      map((nameMap) => {
        const groupedMap = new Map<string, any>();

        for (const item of this.rowData) {
          const userName = nameMap.get(item.userId) ?? item.userId;
          const normalized = {
            ...item,
            userName,
            role: item.role ?? '',
            discipline: item.smeDiscipline?.disciplineCode ?? '',
          };

          const key = `${normalized.userId}_${normalized.role}`;
          if (!groupedMap.has(key)) {
            groupedMap.set(key, {
              userId: normalized.userId,
              userName: normalized.userName,
              role: normalized.role,
              disciplines:
                normalized.role === UserRoles.SME && normalized.discipline
                  ? [normalized.discipline]
                  : [],
              assignmentCount: 1,
            });
          } else {
            const existing = groupedMap.get(key);
            existing.assignmentCount += 1;
            if (
              normalized.role === UserRoles.SME &&
              normalized.discipline &&
              !existing.disciplines.includes(normalized.discipline)
            ) {
              existing.disciplines.push(normalized.discipline);
            }
          }
        }

        return Array.from(groupedMap.values());
      })
    );
  }

  getRoleLabel(role: string): string {
    const roleMap: Record<string, string> = {
      DAEngineer: 'DA Engineer',
      SME: 'SME',
      ProjectContact: 'Project Contact',
    };
    return roleMap[role] || role;
  }
}
